<template>
  <div class="video-materials-page">
    <div class="page-header">
      <v-btn class="back-btn mb-3 px-4 py-4" outlined @click="goBack">
        <img src="./assets/back-icon.svg" alt="" class="back-btn-icon" />
        Назад
      </v-btn>
      <div class="page-header-title-row">
        <div class="page-header-title text-h6 text-sm-h4">
          Видео и материалы
        </div>
      </div>
    </div>

    <div class="section-layout">
      <div class="section-main-wrap">
        <div class="filter-buttons">
          <v-btn
            :class="[
              'filter-btn',
              { 'filter-btn--active': activeFilter === 'video' },
            ]"
            outlined
            depressed
            @click="selectFilter('video')"
          >
            Видео
          </v-btn>
          <v-btn
            :class="[
              'filter-btn',
              { 'filter-btn--active': activeFilter === 'presentations' },
            ]"
            outlined
            depressed
            @click="selectFilter('presentations')"
          >
            Презентации
          </v-btn>
          <v-btn
            :class="[
              'filter-btn',
              { 'filter-btn--active': activeFilter === 'reviews' },
            ]"
            outlined
            depressed
            @click="selectFilter('reviews')"
          >
            Отзывы
          </v-btn>
        </div>

        <div v-if="loading" class="content-cards content-cards--state">
          <v-progress-circular indeterminate color="primary" size="48" />
        </div>
        <div
          v-else-if="loadError"
          class="content-cards content-cards--state error--text"
        >
          {{ loadError }}
        </div>
        <div
          v-else-if="!contentCards.length"
          class="content-cards content-cards--state grey--text text--darken-1"
        >
          Материалов пока нет
        </div>
        <div v-else class="content-cards">
          <VideoMaterialCard
            v-for="item in contentCards"
            :key="item.id"
            :title="item.title"
            :subtitle="item.subtitle"
            :type="item.type"
            :link="item.link"
            :video-url="item.videoUrl"
          />
        </div>
      </div>
      <aside class="section-side-card">
        <div class="section-side-card-inner">
          <div class="section-side-card-header">
            <img
              src="./assets/video-materials-anim.gif"
              alt=""
              class="section-side-card-gif"
            />
            <h3 class="section-side-card-title">Назначение раздела</h3>
          </div>
          <div class="section-side-card-text">
            <p>
              В этом разделе собраны все ключевые материалы, необходимые для
              работы с методом UpMIND+ и взаимодействия с клиентами.
            </p>
            <p>
              Здесь вы найдете: — видеоотзывы клиентов для повышения доверия и
              демонстрации результатов — тестовые сессии для детей и взрослых,
              чтобы знакомить клиентов с форматом метода — видео-презентации с
              объяснением принципов работы — презентационные материалы для
              консультаций и продаж
            </p>
            <p>
              Используйте эти материалы в работе с клиентами, для обучения, а
              также для повышения конверсии на всех этапах взаимодействия.
            </p>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import NSApi from "@/api/NSApi";
import VideoMaterialCard from "./VideoMaterialCard.vue";

const MATERIAL_TYPES = ["video", "presentations", "reviews"];

const DEFAULT_TYPE = "video";

export default {
  name: "VideoAndMaterialsView",
  components: { VideoMaterialCard },
  data() {
    return {
      activeFilter: "video",
      contentCards: [],
      loading: false,
      loadError: null,
    };
  },
  watch: {
    "$route.query.type": {
      handler() {
        const raw = this.$route.query.type;
        const q = raw != null ? String(raw) : "";

        if (!q || !MATERIAL_TYPES.includes(q)) {
          this.activeFilter = DEFAULT_TYPE;
          this.$router
            .replace({
              query: {
                ...this.$route.query,
                type: DEFAULT_TYPE,
              },
            })
            .catch(() => {});
          return;
        }

        this.activeFilter = q;
        this.fetchMaterials();
      },
      immediate: true,
    },
  },
  methods: {
    selectFilter(type) {
      if (!MATERIAL_TYPES.includes(type)) return;
      if (String(this.$route.query.type || "") === type) return;
      this.$router
        .replace({ query: { ...this.$route.query, type } })
        .catch(() => {});
    },
    mapMaterialToCard(m) {
      const link = m.link || "";
      const filePath = m.filePath || "";
      const presentationHref = filePath || link;
      const t = m.type || "";
      const isPresentation = t === "presentations";
      const isVideoOrReview = t === "video" || t === "reviews";
      return {
        id: m.id,
        title: m.title || "",
        subtitle: m.description || "",
        type: m.type,
        link: isPresentation ? presentationHref : link,
        videoUrl: isVideoOrReview ? link : "",
      };
    },
    getAudience() {
      const routeName = this.$route.name || "";
      if (routeName.includes("children")) return "children";
      if (routeName.includes("adults")) return "adults";
      return undefined;
    },
    async fetchMaterials() {
      this.loading = true;
      this.loadError = null;
      try {
        const params = { type: this.activeFilter };
        const audience = this.getAudience();
        if (audience) params.audience = audience;
        const raw = await NSApi.instance.getMaterials(params);
        const list = Array.isArray(raw) ? raw : [];
        const sorted = [...list].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        );
        this.contentCards = sorted.map((m) => this.mapMaterialToCard(m));
      } catch (e) {
        console.error("getMaterials", e);
        this.loadError = "Не удалось загрузить материалы. Попробуйте позже.";
        this.contentCards = [];
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      const userRole = this.$store.state.user?.role;
      const routeName = this.$route.name || "";
      const segment = routeName.includes("adults")
        ? "adults"
        : routeName.includes("children")
        ? "children"
        : null;
      if (userRole === "admin") {
        const name = segment ? `admin-parents-${segment}` : "admin-parents";
        this.$router.push({ name });
      } else if (userRole === "manager") {
        const name = segment ? `manager-parents-${segment}` : "manager-parents";
        this.$router.push({ name });
      } else if (userRole === "company-user" || userRole === "company-owner") {
        const name = segment ? `company-parents-${segment}` : "company-parents";
        this.$router.push({ name });
      } else {
        this.$router.push("/");
      }
    },
  },
};
</script>

<style scoped>
.video-materials-page {
  font-family: "Manrope", sans-serif;
}

.page-header {
  margin: 16px 0 24px 0;
}

.page-header-title {
  font-family: "Manrope", sans-serif;
}

.page-header-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.section-layout {
  display: flex;
  align-items: flex-start;
  gap: 60px;
}

.section-main-wrap {
  flex: 1;
  min-width: 0;
}

.content-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 24px;
}

.content-cards--state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  font-size: 0.9375rem;
}

.section-side-card {
  flex-shrink: 0;
  width: 311px;
  height: 600px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  box-shadow: none;
}

.section-side-card-inner {
  flex: 1;
  background-color: #f0f8f6;
  border-radius: 6px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.section-side-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.section-side-card-gif {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  object-fit: contain;
}

.section-side-card-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}

.section-side-card-text {
  flex: 1;
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.7);
}

.section-side-card-text p {
  margin: 0 0 12px 0;
}

.back-btn {
  font-family: "Manrope", sans-serif !important;
  max-width: 154px;
  border-radius: 20px;
  background-color: white;
  color: #23919e !important;
  border: 1px solid #e0e0e0;
  text-transform: none;
  font-size: 16px !important;
  font-weight: 600 !important;
  line-height: 27px !important;
  letter-spacing: normal;
  padding: 12px 20px !important;
  min-height: auto;
}

.back-btn .v-btn__content {
  font-family: "Manrope", sans-serif !important;
  font-size: 16px;
  line-height: 27px;
  font-weight: 600;
  padding: 0 4px;
}

.back-btn-icon {
  height: 20px;
  width: auto;
  margin-right: 6px;
  vertical-align: middle;
}

.back-btn:hover {
  background-color: #f5f5f5;
  border-color: #e0e0e0;
}

.filter-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-btn {
  font-family: "Manrope", sans-serif !important;
  font-size: 16px !important;
  text-transform: none;
  background-color: white !important;
  border: 1px solid transparent !important;
  border-radius: 20px !important;
  padding: 12px 20px !important;
  min-height: 48px;
}

.filter-btn .v-btn__content {
  font-family: "Manrope", sans-serif !important;
  font-size: 16px !important;
  padding: 0 4px;
}

.filter-btn--active {
  border: 1px solid red !important;
}
</style>
